// src/components/ErrorOverlay.tsx
import React, { useState, useEffect } from 'react';
import { errorReporter, ErrorReport } from '../utils/errorHandlers';

const ErrorOverlay: React.FC = () => {
  const [errors, setErrors] = useState<ErrorReport[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [currentError, setCurrentError] = useState<ErrorReport | null>(null);

  useEffect(() => {
    const checkErrors = () => {
      const currentErrors = errorReporter.getErrors();
      if (currentErrors.length > errors.length) {
        setErrors(currentErrors);
        setCurrentError(currentErrors[0]);
        setIsVisible(true);
      }
    };

    const interval = setInterval(checkErrors, 500);
    return () => clearInterval(interval);
  }, [errors.length]);

  const handleDismiss = () => {
    setIsVisible(false);
    setCurrentError(null);
  };

  const handleClearAll = () => {
    errorReporter.clearErrors();
    setErrors([]);
    setIsVisible(false);
    setCurrentError(null);
  };

  const handleNext = () => {
    const currentIndex = errors.findIndex(e => e === currentError);
    const nextIndex = (currentIndex + 1) % errors.length;
    setCurrentError(errors[nextIndex]);
  };

  const handlePrev = () => {
    const currentIndex = errors.findIndex(e => e === currentError);
    const prevIndex = currentIndex === 0 ? errors.length - 1 : currentIndex - 1;
    setCurrentError(errors[prevIndex]);
  };

  if (!isVisible || !currentError || process.env.NODE_ENV !== 'development') {
    return null;
  }

  const currentIndex = errors.findIndex(e => e === currentError);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-red-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <div>
              <h2 className="text-lg font-semibold">Runtime Error</h2>
              <p className="text-sm opacity-90">
                {errors.length === 1 ? '1 error' : `${errors.length} errors`} • 
                Error {currentIndex + 1} of {errors.length}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {errors.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="p-1 hover:bg-red-700 rounded"
                  title="Previous error"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                </button>
                <button
                  onClick={handleNext}
                  className="p-1 hover:bg-red-700 rounded"
                  title="Next error"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
                  </svg>
                </button>
              </>
            )}
            
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-red-700 rounded"
              title="Dismiss"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Error Message */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Message</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-mono text-sm break-words">
                {currentError.message}
              </p>
            </div>
          </div>

          {/* Stack Trace */}
          {currentError.stack && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Stack Trace</h3>
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                  {currentError.stack}
                </pre>
              </div>
            </div>
          )}

          {/* Error Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Details</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">Type:</span>
                  <span className="ml-2 text-blue-700 capitalize">{currentError.type}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">Timestamp:</span>
                  <span className="ml-2 text-blue-700">{currentError.timestamp.toLocaleString()}</span>
                </div>
                {currentError.url && (
                  <div className="col-span-2">
                    <span className="font-medium text-blue-800">File:</span>
                    <span className="ml-2 text-blue-700 font-mono text-xs break-all">
                      {currentError.url}
                      {currentError.lineNumber && `:${currentError.lineNumber}`}
                      {currentError.columnNumber && `:${currentError.columnNumber}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
          <div className="text-sm text-gray-600">
            Fix the error above to continue development
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleClearAll}
              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorOverlay;